// =============================================================================
//  FrameworkBadge - badge colore par framework reglementaire.
//
//  Couleurs distinctes pour faciliter le scan visuel des references :
//   - NIS2     : bleu acier (primary brand) = serieux reglementaire
//   - ISO27001 : teal                       = standard international
//   - CIS-M365 : ambre                      = benchmark technique operationnel
//   - CyFun    : violet                     = framework EU regional (CCB BE)
// =============================================================================

import { Badge, tokens } from '@fluentui/react-components';
import { ControlReference } from '../domain/types';

interface FrameworkBadgeProps {
  /** Reference complete - le badge affiche framework + ref + (cyfunLevel si applicable). */
  reference: ControlReference;
  /** Compact = badge plus petit, sans le label. */
  compact?: boolean;
}

const FRAMEWORK_COLORS: Record<ControlReference['framework'], { fg: string; bg: string; full: string }> = {
  'NIS2':     { fg: '#0A111E', bg: '#DCE5EE', full: 'NIS2 (UE 2022/2555)' },
  'ISO27001': { fg: '#114042', bg: '#DCF0F1', full: 'ISO/IEC 27001:2022' },
  'CIS-M365': { fg: '#875014', bg: '#FAEDD9', full: 'CIS Microsoft 365 Foundations v3' },
  'CIS-Azure':{ fg: '#1B5E91', bg: '#DDEBF7', full: 'CIS Azure Foundations Benchmark v2.1' },
  'CyFun':    { fg: '#3B255C', bg: '#EBE3F5', full: 'CyFun (CCB Belgium, NIST CSF-aligned)' },
};

const CYFUN_LEVEL_COLORS: Record<NonNullable<ControlReference['cyfunLevel']>, { fg: string; bg: string }> = {
  Basic:     { fg: '#1F7A45', bg: '#E6F4EC' },   // vert : niveau de base
  Important: { fg: '#A86317', bg: '#FDF1E2' },   // ambre : intermediaire
  Essential: { fg: '#B82329', bg: '#FBE7E7' },   // rouge profond : exigence maximale
};

export function FrameworkBadge({ reference, compact = false }: FrameworkBadgeProps) {
  const palette = FRAMEWORK_COLORS[reference.framework];

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }} title={palette.full}>
      <Badge
        appearance="filled"
        style={{
          backgroundColor: palette.bg,
          color:           palette.fg,
          fontWeight:      600,
          fontSize:        compact ? 10 : 11,
        }}
      >
        <span style={{ opacity: 0.7, marginRight: 4 }}>{reference.framework}</span>
        <span>{reference.ref}</span>
      </Badge>

      {reference.cyfunLevel && (
        <Badge
          appearance="outline"
          style={{
            backgroundColor: CYFUN_LEVEL_COLORS[reference.cyfunLevel].bg,
            color:           CYFUN_LEVEL_COLORS[reference.cyfunLevel].fg,
            fontWeight:      600,
            fontSize:        compact ? 9 : 10,
            border:          `1px solid ${CYFUN_LEVEL_COLORS[reference.cyfunLevel].fg}`,
          }}
        >
          {reference.cyfunLevel}
        </Badge>
      )}
    </span>
  );
}

/** Variante texte uniquement (pour les pages denses comme la liste de controles). */
export function FrameworkInlineText({ reference }: { reference: ControlReference }) {
  const palette = FRAMEWORK_COLORS[reference.framework];
  return (
    <span
      title={reference.label}
      style={{
        fontSize:   11.5,
        color:      tokens.colorNeutralForeground3,
      }}
    >
      <strong style={{ color: palette.fg }}>{reference.framework}</strong>{' '}
      {reference.ref}
      {reference.cyfunLevel && (
        <span style={{ marginLeft: 4, fontSize: 9.5, color: CYFUN_LEVEL_COLORS[reference.cyfunLevel].fg, fontWeight: 600 }}>
          ({reference.cyfunLevel})
        </span>
      )}
    </span>
  );
}
